import React, { useMemo, useRef, useState } from 'react';
import {
    Box, Button, Chip, CircularProgress, IconButton, MenuItem, Tab, Table, TableBody, TableCell,
    TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import { Delete as DeleteIcon, Download as DownloadIcon, UploadFile as UploadFileIcon } from '@mui/icons-material';
import { AuthService } from '../../services/auth.service';
import { StyleService } from '../../services/style.service';
import { IStyleAttachment, STYLE_DOCUMENT_KINDS, StyleDocumentKind } from '../../interfaces/garments/style.interface';
import { useToast } from '../../context/ToastContext';

interface StyleAttachmentsProps {
    styleId: string;
    attachments: IStyleAttachment[];
    canManage: boolean;
    onChanged: () => void;
}

// Mirrors the shared backend FileStorageService.ALLOWED_CONTENT_TYPES + the 10MB
// spring.servlet.multipart.max-file-size cap (see StyleAttachmentService's Javadoc) — stated up
// front (R11/M1) so an unsupported file is refused here rather than surfacing a backend 400.
const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
const ACCEPTED_EXTENSIONS = '.pdf,.jpg,.jpeg,.png,.doc,.docx';
const MAX_SIZE_BYTES = 10 * 1024 * 1024;

// 'ALL' is the frontend-only tab value; every other value is a StyleDocumentKind. Tabs filter the
// already-loaded `attachments` array client-side (merch-commercial close-out T4) — no refetch per tab.
const TAB_KINDS: ('ALL' | StyleDocumentKind)[] = ['ALL', ...STYLE_DOCUMENT_KINDS.map(k => k.value)];

const formatSize = (bytes?: number | null): string => {
    if (bytes == null) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const kindLabel = (kind: StyleDocumentKind): string =>
    STYLE_DOCUMENT_KINDS.find(k => k.value === kind)?.label ?? kind;

/** Attachment upload/list/download/delete for a style (garments-merchandising T8; document-kind
 *  tabs + versioning added in merch-commercial close-out T4), spec R11/M1: the upload control
 *  states the accepted types and the 10MB cap up front. */
export function StyleAttachments({ styleId, attachments, canManage, onChanged }: StyleAttachmentsProps) {
    const toast = useToast();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [downloadingId, setDownloadingId] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [tab, setTab] = useState<'ALL' | StyleDocumentKind>('ALL');
    const [uploadKind, setUploadKind] = useState<StyleDocumentKind>('TECH_PACK');
    const [revisionLabel, setRevisionLabel] = useState('');
    const [notes, setNotes] = useState('');

    const institutionId = () => AuthService.getSelectedInstitution();

    const countsByKind = useMemo(() => {
        const counts: Partial<Record<'ALL' | StyleDocumentKind, number>> = { ALL: attachments.length };
        for (const kind of STYLE_DOCUMENT_KINDS) {
            counts[kind.value] = attachments.filter(a => a.documentKind === kind.value).length;
        }
        return counts;
    }, [attachments]);

    const visibleAttachments = useMemo(
        () => (tab === 'ALL' ? attachments : attachments.filter(a => a.documentKind === tab)),
        [attachments, tab]
    );

    const handleFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;

        if (!ACCEPTED_TYPES.includes(file.type)) {
            toast.showError('Unsupported file type. Accepted: PDF, JPG, PNG, DOC, DOCX.');
            return;
        }
        if (file.size > MAX_SIZE_BYTES) {
            toast.showError('File is too large. The limit is 10MB.');
            return;
        }

        const instId = institutionId();
        if (!instId) return;
        setUploading(true);
        try {
            await StyleService.uploadAttachment(instId, styleId, file, uploadKind, revisionLabel.trim() || undefined, notes.trim() || undefined);
            toast.showSuccess('Attachment uploaded');
            setRevisionLabel('');
            setNotes('');
            onChanged();
        } catch (err) {
            toast.showApiError(err, 'Failed to upload attachment');
        } finally {
            setUploading(false);
        }
    };

    const handleDownload = async (attachment: IStyleAttachment) => {
        const instId = institutionId();
        if (!instId) return;
        setDownloadingId(attachment.id);
        try {
            await StyleService.downloadAttachment(instId, styleId, attachment.id, attachment.fileName);
        } catch (err) {
            toast.showApiError(err, 'Failed to download attachment');
        } finally {
            setDownloadingId(null);
        }
    };

    const handleDelete = async (attachment: IStyleAttachment) => {
        if (!window.confirm(`Delete attachment "${attachment.fileName}"?`)) return;
        const instId = institutionId();
        if (!instId) return;
        setDeletingId(attachment.id);
        try {
            await StyleService.deleteAttachment(instId, styleId, attachment.id);
            toast.showSuccess('Attachment deleted');
            onChanged();
        } catch (err) {
            toast.showApiError(err, 'Failed to delete attachment');
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle1">Tech pack &amp; attachments</Typography>
            </Box>

            {canManage && (
                <Box data-help-section="merchandising.attachments.upload" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start', mb: 2 }}>
                    <TextField
                        select
                        data-help="merchandising.attachments.kind"
                        label="Kind"
                        value={uploadKind}
                        onChange={e => setUploadKind(e.target.value as StyleDocumentKind)}
                        disabled={uploading}
                        sx={{ flex: '1 1 160px', maxWidth: 220 }}
                    >
                        {STYLE_DOCUMENT_KINDS.map(k => (
                            <MenuItem key={k.value} value={k.value}>{k.label}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        data-help="merchandising.attachments.revision"
                        label="Revision"
                        placeholder="Rev C"
                        value={revisionLabel}
                        onChange={e => setRevisionLabel(e.target.value)}
                        inputProps={{ maxLength: 64 }}
                        disabled={uploading}
                        sx={{ flex: '1 1 160px', maxWidth: 220 }}
                    />
                    <TextField
                        data-help="merchandising.attachments.notes"
                        label="Notes"
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                        inputProps={{ maxLength: 500 }}
                        disabled={uploading}
                        sx={{ flex: '1 1 200px', maxWidth: 320 }}
                    />
                    <Button
                        size="small"
                        component="label"
                        data-help="merchandising.attachments.upload.button"
                        startIcon={uploading ? <CircularProgress size={16} /> : <UploadFileIcon />}
                        disabled={uploading}
                        sx={{ mt: 1 }}
                    >
                        Upload
                        <input
                            ref={fileInputRef}
                            type="file"
                            hidden
                            accept={ACCEPTED_EXTENSIONS}
                            onChange={handleFileChosen}
                            data-testid="style-attachment-input"
                        />
                    </Button>
                </Box>
            )}
            <Typography variant="caption" color="text.secondary">
                PDF, JPEG, PNG, DOC, DOCX — max 10MB
            </Typography>

            <Tabs
                data-help="merchandising.attachments.tabs"
                value={tab}
                onChange={(_e, value) => setTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ mt: 1, mb: 1, borderBottom: 1, borderColor: 'divider' }}
            >
                {TAB_KINDS.map(kind => (
                    <Tab
                        key={kind}
                        value={kind}
                        label={(
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {kind === 'ALL' ? 'All' : kindLabel(kind)}
                                <Chip size="small" label={countsByKind[kind] ?? 0} />
                            </Box>
                        )}
                    />
                ))}
            </Tabs>

            <Box sx={{ overflowX: 'auto', maxWidth: '100%' }}>
            <Table size="small" data-help-section="merchandising.attachments.table">
                <TableHead>
                    <TableRow>
                        <TableCell data-help="merchandising.attachments.col.file">File</TableCell>
                        <TableCell data-help="merchandising.attachments.col.kind">Kind</TableCell>
                        <TableCell data-help="merchandising.attachments.col.version">Version</TableCell>
                        <TableCell data-help="merchandising.attachments.col.revision">Revision</TableCell>
                        <TableCell data-help="merchandising.attachments.col.notes">Notes</TableCell>
                        <TableCell align="right" data-help="merchandising.attachments.col.actions">Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {visibleAttachments.map(a => (
                        <TableRow key={a.id}>
                            <TableCell>
                                <Typography variant="body2">{a.fileName}</Typography>
                                <Typography variant="caption" color="text.secondary">{formatSize(a.sizeBytes)}</Typography>
                            </TableCell>
                            <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {kindLabel(a.documentKind)}
                                    {a.latestForKind && <Chip size="small" color="primary" label="Latest" />}
                                </Box>
                            </TableCell>
                            <TableCell>{a.versionNo}</TableCell>
                            <TableCell>{a.revisionLabel ?? ''}</TableCell>
                            <TableCell sx={{ whiteSpace: 'normal', minWidth: 140 }}>{a.notes ?? ''}</TableCell>
                            <TableCell align="right">
                                <IconButton
                                    size="small"
                                    data-help="merchandising.attachments.download"
                                    onClick={() => handleDownload(a)}
                                    disabled={downloadingId === a.id}
                                    aria-label={`Download ${a.fileName}`}
                                >
                                    <DownloadIcon fontSize="small" />
                                </IconButton>
                                {canManage && (
                                    <IconButton
                                        size="small"
                                        data-help="merchandising.attachments.delete"
                                        onClick={() => handleDelete(a)}
                                        disabled={deletingId === a.id}
                                        aria-label={`Delete ${a.fileName}`}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                    {visibleAttachments.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={6}>
                                <Typography variant="body2" color="text.secondary">
                                    No attachments yet.
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
            </Box>
        </Box>
    );
}

export default StyleAttachments;
